import clsx from 'clsx';
import {
  type ChangeEvent,
  forwardRef,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

import { useToggleButtonGroupContext } from './hooks/useToggleButtonGroupContext.js';
import { Prefix } from './Prefix.js';
import * as styles from './styles.sty.js';
import { Suffix } from './Suffix.js';
import { VisuallyHidden } from '../../core/components/visually-hidden/VisuallyHidden.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useId } from '../../core/hooks/useId.js';
import { useSlots } from '../../core/hooks/useSlots.js';
import { focusRingStatic } from '../../core/styles/focusRingStatic.sty.js';
import { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import { DOMProps } from '../../core/types/dom.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { isDev } from '../../core/utils/is-node-environment.js';
import { mergeProps as _mergeProps } from '../../core/utils/merge-props.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { Tooltip } from '../../overlays/tooltip/Tooltip.js';
import { Text } from '../../typography/text/Text.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import { useFormFieldValidation } from '../forms-core/hooks/useFormFieldValidation.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import type { FormControlRef } from '../shared-types.js';
import { getValidationState } from '../validation/validation-builders.js';

//#region Typings
/**
 * Accepted properties for the ToggleButtonGroup Item.
 * @public
 */
export type ToggleButtonGroupItemProps = FormControlProps<string, never> &
  PropsWithChildren &
  DOMProps &
  AriaLabelingProps &
  StylingProps &
  DataTestId &
  BehaviorTrackingProps & {
    /** When a tooltip is provided, text is shown on hover. */
    tooltip?: string;

    /**
     * Zero or more items in a group can be checked.
     * @defaultValue false
     * @deprecated unused, will be removed
     */
    checked?: boolean;

    /**
     * Indicates if the mover is currently over the item.
     * @defaultValue false
     * @deprecated unused, will be removed
     */
    hovered?: boolean;
  };
//#endregion

/**
 * ToggleButtonGroup Item component.
 * @public
 */
export const Item = forwardRef<FormControlRef, ToggleButtonGroupItemProps>(
  (props, forwardedRef) => {
    const fallbackId = useId();
    const {
      id = fallbackId,
      disabled: propDisabled,
      readOnly: propReadOnly,
      tooltip,
      children,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      value,
      ...remainingProps
    } = props;

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);
    const {
      'data-dt-name': dataDtName = 'Toggle',
      ...remainingBehavioralTrackingProps
    } = behavioralTrackingProps;

    const {
      name,
      isRequired,
      hasError,
      isDisabled: disabledContext,
      ariaDisabled,
      selectedValue,
      isReadOnly: isReadOnlyContext,
      onChange,
      onValidityChange,
    } = useToggleButtonGroupContext();

    const labelRef = useRef<HTMLLabelElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const isDisabled = disabledContext || propDisabled;
    const isReadOnly = isReadOnlyContext || propReadOnly;
    const checked = selectedValue === value;

    const color = hasError ? 'critical' : 'neutral';

    const { dispatch: messagesContextDispatch } = useFormFieldMessagesContext();

    const {
      onChange: validationOnChange,
      onInvalid: validationOnInvalid,
      onBlur: validationOnBlur,
      ariaProps,
    } = useFormFieldValidation(
      inputRef,
      { isRequired, selectedValue: selectedValue ?? undefined },
      undefined,
      id,
      onValidityChange,
    );

    /** Register every single input to the form error context to ensure proper validation. */
    useEffect(() => {
      messagesContextDispatch?.({
        type: FormFieldMessagesAction.ADDFORMCONTROLID,
        payload: { controlId: id },
      });

      return () =>
        messagesContextDispatch?.({
          type: FormFieldMessagesAction.REMOVEFORMCONTROLID,
          payload: { controlId: id },
        });
    }, [id, messagesContextDispatch]);

    const [slots, freeform] = useSlots(children, {
      prefix: Prefix,
      suffix: Suffix,
    });

    useImperativeHandle(forwardedRef, (): FormControlRef<HTMLLabelElement> => {
      return {
        element: labelRef.current,
        inputRef: inputRef.current,
        focus() {
          inputRef.current?.focus();
        },
        validate() {
          inputRef.current?.checkValidity();
        },
        // DEPRECATION: [APPDEV-18178] BREAKING-CHANGE: Remove validateAsync() and change validate() to return Promise<ValidationState>.
        validateAsync: async () => {
          inputRef.current?.checkValidity();
          return getValidationState(inputRef.current, 'manual');
        },
      };
    });

    const scrollTabIntoView = useCallback(
      (behavior: 'smooth' | 'instant') => {
        if (!checked || !labelRef.current) {
          return;
        }

        const parent = labelRef.current.parentElement;
        if (!parent) {
          return;
        }

        const parentRect = parent.getBoundingClientRect();
        const targetRect = labelRef.current.getBoundingClientRect();

        const currentLeft = parent.scrollLeft;
        const deltaLeft = targetRect.left - parentRect.left;
        const deltaRight = targetRect.right - parentRect.right;

        let nextLeft = currentLeft;
        if (deltaLeft < 0) {
          nextLeft += deltaLeft;
        } else if (deltaRight > 0) {
          nextLeft += deltaRight;
        }

        parent.scrollTo?.({ left: nextLeft, behavior: behavior });
      },
      [checked],
    );

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        if (isReadOnly) {
          return;
        }

        if (value !== undefined) {
          onChange?.(value, e);
        }

        validationOnChange?.();
        onValidityChange?.(getValidationState(inputRef.current, 'change'));
      },
      [isReadOnly, value, onChange, validationOnChange, onValidityChange],
    );

    useEffect(() => {
      if (checked && labelRef.current) {
        scrollTabIntoView('instant');
      }
      // Instantly scrolls to the checked item only on the first render.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      const parent = labelRef.current?.parentElement;
      if (!parent) {
        return;
      }

      let prevWidth = parent.offsetWidth;
      let debounceTimer: number | undefined;

      const resizeObserver = new window.ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.target === parent && parent.offsetWidth !== prevWidth) {
            prevWidth = parent.offsetWidth;
            window.clearTimeout(debounceTimer);
            debounceTimer = window.setTimeout(() => {
              scrollTabIntoView('smooth');
            }, 300);
          }
        }
      });
      resizeObserver.observe(parent);

      scrollTabIntoView('smooth');

      return () => {
        resizeObserver.disconnect();
        window.clearTimeout(debounceTimer);
      };
    }, [checked, scrollTabIntoView]);

    if (isDev() && tooltip === undefined && freeform.length === 0) {
      // eslint-disable-next-line no-console
      console.warn(
        'A text to indicate the value is required. It can be provided by a tooltip.',
      );
    }

    const toggleItem = (
      <label
        data-testid={dataTestId}
        htmlFor={id}
        ref={labelRef}
        className={clsx(
          styles.item,
          {
            [styles.checked]: checked,
            [styles.disabled]: isDisabled || ariaDisabled,
            [styles.readOnly]: isReadOnly,
          },
          focusRingStatic({
            when: 'focus-within-visible',
            disabled: isDisabled || ariaDisabled,
            color: color,
          }),
          consumerClassName,
        )}
        style={consumerStyle}
        data-dt-name={dataDtName}
        {...remainingBehavioralTrackingProps}
        {...restProps}
      >
        <VisuallyHidden>
          <input
            id={id}
            type="radio"
            ref={inputRef}
            name={name}
            value={value}
            disabled={isDisabled}
            aria-disabled={ariaDisabled}
            readOnly={isReadOnly}
            aria-label={tooltip}
            checked={checked}
            onChange={handleChange}
            required={isRequired}
            onInvalid={validationOnInvalid}
            {..._mergeProps(restProps, {
              ...ariaProps,
              onBlur: validationOnBlur,
            })}
          />
        </VisuallyHidden>

        {slots.prefix}
        {freeform.length > 0 && (
          <Flex flexDirection="column" gap={0} className={styles.textContainer}>
            <Text
              as="span"
              textStyle={checked ? 'base-emphasized' : 'base'}
              className={styles.text}
            >
              {freeform}
            </Text>
            {/** Visually hidden text to prevent layout shift when the item is checked and the text becomes emphasized */}
            <Text
              as="span"
              textStyle="base-emphasized"
              className={clsx(styles.hidden, styles.text)}
              aria-hidden
            >
              {freeform}
            </Text>
          </Flex>
        )}
        {slots.suffix}
      </label>
    );

    return tooltip ? (
      <Tooltip text={tooltip}>{toggleItem}</Tooltip>
    ) : (
      toggleItem
    );
  },
);

(Item as typeof Item & { displayName: string }).displayName =
  'ToggleButtonGroup.Item';
